import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  AlertCircle,
  Plus,
  Eye,
  Edit,
  Trash2,
  Briefcase,
} from "lucide-react";
import CareerViewModal from "../../components/AdminComponents/career/CareerViewModal";
import CareerCreateModal from "../../components/AdminComponents/career/CareerCreateModal";
import CareerUpdateModal from "../../components/AdminComponents/career/CareerUpdateModal";
import CareersSkeleton from './../../components/AdminComponents/career/CareersSkeleton';
import Swal from "sweetalert2";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const AuthCareer = () => {
  const [careers, setCareers] = useState([]);
  const [filteredCareers, setFilteredCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [viewCareerModal, setViewCareerModal] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [createCareerModal, setCreateCareerModal] = useState(false);
  const [updateCareerModal, setUpdateCareerModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/career/all-career`
      );

      if (response.data.success) {
        setCareers(response.data.data);
        setFilteredCareers(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching careers:", err);
      setError(err.response?.data?.message || "Failed to load careers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCareer = () => {
    setCreateCareerModal(true);
  };

  const handleViewCareer = (career) => {
    setViewCareerModal(true);
    setSelectedCareer(career);
  };

  const handleUpdateCareer = (career) => {
    setSelectedCareer(career);
    setUpdateCareerModal(true);
  };

  const handleDeleteCareer = async (career) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: 'This will delete the career permanently!',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      confirmButtonColor: "#0bb6d9",
      cancelButtonColor: "#d33",
    });

    if (confirm.isConfirmed) {
      try {
        setDeleteLoading(career._id);

        await axios.delete(
          `${process.env.REACT_APP_API_URL}/career/delete-career/${career._id}`,
          // { withCredentials: true }
        );

        // Update both careers arrays
        const updatedCareers = careers.filter((c) => c._id !== career._id);
        setCareers(updatedCareers);
        setFilteredCareers(updatedCareers);

        // Adjust current page if needed
        if (currentCareers.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }

        Swal.fire("Deleted!", "Career has been deleted successfully.", "success");
      } catch (err) {
        console.error("Error deleting career:", err);
        Swal.fire("Error!", "Failed to delete career.", "error");
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  // Pagination calculations
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentCareers = filteredCareers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCareers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // CORRECTED getPageNumbers function
  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Error Loading Careers
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchCareers}
            className="bg-[#0bb6d9] text-white px-6 py-2 rounded-lg hover:bg-[#069ab7] transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              <span className="text-[#0bb6d9]">Careers</span>
            </h1>
            <p className="text-gray-600">
              Showing {currentCareers.length} of {filteredCareers.length} careers
            </p>
          </div>

          <button
            onClick={handleCreateCareer}
            className="flex items-center gap-2 bg-[#0bb6d9] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#069ab7] transition-colors duration-300 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Create Career
          </button>
        </div>

        {
          loading ? <CareersSkeleton /> : <>
            {currentCareers.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-white rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Briefcase className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                  No Careers Available
                </h3>
                <p className="text-gray-600 mb-6">
                  Create your first career to get started
                </p>
                <button
                  onClick={handleCreateCareer}
                  className="inline-flex items-center gap-2 bg-[#0bb6d9] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#069ab7] transition"
                >
                  <Plus className="w-5 h-5" />
                  Create First Career
                </button>
              </div>
            ) : (
              /* Careers Grid */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentCareers.map((career) => (
                  <div
                    key={career._id}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl"
                  >
                    <div className="p-6">
                       <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                        {career.position}
                      </h3>
                      <h3 className="text-sm font-bold text-gray-800 mb-3 line-clamp-2">
                        {career.company?.companyName}
                      </h3>
                     

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewCareer(career)}
                          className="flex-1 flex items-center justify-center gap-2 bg-[#0bb6d9] text-white py-2 px-4 rounded-lg font-semibold hover:bg-[#069ab7] transition-colors duration-300 shadow-md hover:shadow-lg"
                        >
                          <Eye className="w-4 h-4 md:flex hidden" />
                          View
                        </button>
                        <button
                          onClick={() => handleUpdateCareer(career)}
                          className="flex-1 flex items-center justify-center gap-2 bg-blue-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-600 transition-colors duration-300 shadow-md hover:shadow-lg"
                        >
                          <Edit className="w-4 h-4 md:flex hidden" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCareer(career)}
                          disabled={deleteLoading === career._id}
                          className="flex-1 flex items-center justify-center gap-2 bg-red-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleteLoading === career._id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 md:flex hidden" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        }

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md transition-colors ${currentPage === page
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}

        {viewCareerModal && (
          <CareerViewModal
            career={selectedCareer}
            onClose={() => setViewCareerModal(false)}
            onClick={() => {
              setUpdateCareerModal(true);
              setViewCareerModal(false);
            }}
          />
        )}

        {updateCareerModal && (
          <CareerUpdateModal
            career={selectedCareer}
            onClose={() => setUpdateCareerModal(false)}
            onSuccess={fetchCareers}
          />
        )}

        {createCareerModal && (
          <CareerCreateModal
            onClose={() => setCreateCareerModal(false)}
            onSuccess={fetchCareers}
          />
        )}
      </div>
    </div>
  );
};

export default AuthCareer;